import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Text } from '@components/Text/Text';
import { css } from '@emotion/react';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Button } from '@components/Button/Button';
import { useStrings } from '@hooks/useStrings';

export interface CodeBlockProps {
  /**
   * The code string to display.
   */
  content: string;

  /**
   * Styles overrides.
   */
  className?: string;

  /**
   * If set to true, displays the copyAction button
   */
  allowCopy?: boolean;

  /**
   * Specifies text to show on copy button.
   * Defaults to 'Copy'.
   */
  copyText?: string;

  /**
   * Specifies feedback text to show when code is copied.
   * Defaults to 'Copied!'.
   */
  copyFeedback?: string;
}

const Container = styled.div`
  display: flex;
  position: relative;
  border-radius: ${tokens.radius.l};
  background-color: ${tokens.BRAND_COLORS.neutral_100};
`;

const LineNumbers = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${tokens.spacer.x2} ${tokens.spacer.x1};
  width: ${tokens.spacer.x4};
  background-color: ${tokens.BRAND_COLORS.neutral_200};
  border-radius: ${tokens.radius.l} 0 0 ${tokens.radius.l};
  text-align: right;
`;

const MultilineCode = styled.pre`
  flex-grow: 1;
  padding: ${tokens.spacer.x2};
  overflow-x: auto;
  margin: 0;
  border: none;
  background: transparent;
`;

const Code = styled.code`
  ${tokens.text('caption')}
  font-family: monospace;
  color: ${tokens.color.text_default};
  line-height: ${tokens.spacer.x2pt5};
`;

const CopyButton = styled(Button)`
  position: absolute;
  right: ${tokens.spacer.x1};
  top: ${tokens.spacer.x1};
`;

/**
 * Displays a code block with line numbers.
 * It can also provide a CopyAction button which allows the user to copy the block's content.
 *
 * {@link https://zeroheight.com/76c439891/p/04bcfb-code-block Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-codeblock--basic-code-block Code documentation}
 *
 * @example
 * <CodeBlock content={`const code='Hello';`} />
 */
export const CodeBlock = ({
  content,
  className,
  copyText,
  copyFeedback,
  allowCopy = false,
}: CodeBlockProps): JSX.Element => {
  const { isCopied, copy } = useCopyToClipboard(content);
  const { actions } = useStrings();

  const finalCopyText = copyText || actions.copy;
  const finalCopyFeedback = copyFeedback || actions.copied;

  return (
    <Container className={className}>
      <LineNumbers>
        {content.split('\n').map((_, index) => (
          <Text
            key={index}
            as="span"
            textType="caption"
            textColor="text_light"
            css={css`
              line-height: ${tokens.spacer.x2pt5};
              font-family: monospace;
            `}
          >
            {index + 1}
          </Text>
        ))}
      </LineNumbers>
      <MultilineCode>
        <Code translate="no">{content}</Code>
      </MultilineCode>
      {allowCopy && (
        <CopyButton
          kind="secondary"
          onClick={copy}
          icon={isCopied ? 'check' : 'copy'}
          size="s"
          css={css`
            background-color: ${tokens.color.neutral_100};
          `}
        >
          {isCopied ? finalCopyFeedback : finalCopyText}
        </CopyButton>
      )}
    </Container>
  );
};

CodeBlock.displayName = 'CodeBlock';
